import { css } from '@emotion/css';
import { cssObj } from '@/css/resources/functions.css';
import {
	resetButton,
	resetFirstLastMargins,
} from '@/css/resources/placeholders.css';
import { transitions, zIndex } from '@/css/resources/variables.css';
import {
	expandLink,
	fluidValues,
	fontStyle,
	lineClamp,
	fluidBorderWidth,
	fluidBorderRadius,
} from '@/css/resources/mixins.css';

const getDynamicStyles = (styles) =>
	cssObj(
		fluidBorderWidth(styles?.border?.width),
		fluidBorderRadius(styles?.border?.radius),
		{
			width:
				styles?.minWidth &&
				styles?.maxWidth &&
				`clamp(${styles?.minWidth}, 100%, ${styles?.maxWidth})`,
			background: styles?.backgroundColor,
			borderColor: styles?.border?.color,

			'.supt-cardResource__uptitleWrapper': cssObj(
				fluidValues(
					'paddingRight',
					styles?.padding?.right?.[0],
					styles?.padding?.right?.[1]
				),
				fluidValues(
					'paddingLeft',
					styles?.padding?.left?.[0],
					styles?.padding?.left?.[1]
				),
				{
					background: styles?.uptitle?.backgroundColor,
				}
			),

			'.supt-cardResource__uptitle': fontStyle({
				...styles?.uptitle?.typography,
				textAlign: styles?.uptitle?.textAlignment,
				color: styles?.uptitle?.color,
			}),

			'.supt-cardResource__inner': cssObj(
				fluidValues(
					'paddingRight',
					styles?.padding?.right?.[0],
					styles?.padding?.right?.[1]
				),
				fluidValues(
					'paddingBottom',
					styles?.padding?.bottom?.[0],
					styles?.padding?.bottom?.[1]
				),
				fluidValues(
					'paddingLeft',
					styles?.padding?.left?.[0],
					styles?.padding?.left?.[1]
				)
			),

			'.supt-cardResource__content': cssObj(
				fluidValues(
					'paddingTop',
					styles?.padding?.top?.[0],
					styles?.padding?.top?.[1]
				)
			),

			'.supt-cardResource__imagePlay': cssObj(
				fluidValues(
					'width',
					styles?.play?.width?.[0],
					styles?.play?.width?.[1]
				),
				fluidValues(
					'height',
					styles?.play?.height?.[0],
					styles?.play?.height?.[1]
				),
				{
					circle: {
						fill: styles?.play?.background,
					},
					path: {
						fill: styles?.play?.foreground,
					},
				}
			),

			'.supt-cardResource__image': cssObj(
				fluidValues(
					'maxHeight',
					styles?.image?.height?.[0],
					styles?.image?.height?.[1]
				)
			),

			'.supt-cardResource__title': fontStyle({
				...styles?.title?.typography,
				textAlign: styles?.title?.textAlignment,
				color: styles?.title?.color,
			}),

			'.supt-cardResource__description': fontStyle({
				...styles?.description?.typography,
				textAlign: styles?.description?.textAlignment,
				color: styles?.description?.color,
			}),

			'.supt-cardResource__cta': fontStyle({
				...styles?.cta?.typography,
				textAlign: styles?.cta?.textAlignment,
				color: styles?.cta?.color,
			}),
		}
	);

const getModalStyles = () =>
	css({
		'.supt-modal__inner': {
			width: '100%',
			height: 0,
			paddingTop: '56.25%' /* ratio 16 / 9 */,
			iframe: {
				position: 'absolute',
				top: 0,
				left: 0,
				width: '100%',
				height: '100%',
			},
		},
	});

const getStyles = (styles) =>
	css(getDynamicStyles(styles), {
		display: 'flex',
		flexDirection: 'column',
		borderStyle: 'solid',
		overflow: 'hidden',

		'.supt-cardResource__uptitleWrapper': {
			display: 'flex',
		},
		'.supt-cardResource__uptitle': {
			width: '100%',
		},

		'.supt-cardResource__inner': {
			height: '100%',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flex-start',
		},

		'.supt-cardResource__content': {
			display: 'block',
			width: '100%',
		},

		'.supt-cardResource__imageWrapper': {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},

		'.supt-cardResource__imagePlay': {
			position: 'absolute',
			zIndex: 1,
			transformOrigin: 'center center',
		},

		'.supt-cardResource__image': {
			display: 'inline-flex',

			img: {
				width: '100%',
				objectFit: 'cover',
			},
		},

		'.supt-cardResource__title': cssObj(
			resetFirstLastMargins,
			lineClamp(3)
		),

		'.supt-cardResource__description': resetFirstLastMargins,

		'.supt-cardResource__ctaWrapper': {
			marginTop: 'auto',
			width: '100%',
		},

		'.supt-cardResource__cta': cssObj(resetButton, {
			display: 'block',
			marginBottom: '0px',

			'&.supt-cardResource__cta--withArrow': {
				'&::after': {
					display: 'inline-block',
					marginLeft: '0.5em',
					fontSize: '0.85em',
					content: '"▶︎"',
				},
			},
		}),

		/* Modifiers */
		'&.supt-cardResource--video': {
			'.supt-cardResource__title': lineClamp(2),
		},

		/* with link */
		'&.supt-withClick': cssObj(expandLink(), expandLink('button'), {
			transition: `all ${transitions.mid} ease-out`,

			'.supt-cardResource__uptitleWrapper, .supt-cardResource__uptitle, .supt-cardResource__title, .supt-cardResource__description, .supt-cardResource__cta, .supt-cardResource__cta::after, .supt-cardResource__imagePlay, .supt-cardResource__imagePlay circle, .supt-cardResource__imagePlay path':
				{
					transition: `all ${transitions.mid} ease-out`,
				},

			'&:hover, &:focus-within': cssObj(
				getDynamicStyles(styles?.states?.hover),
				{
					'.supt-cardResource__cta--withArrow::after': {
						transform: 'translateX(2px)',
					},

					'&.supt-cardResource--video': {
						'.supt-cardResource__imagePlay': {
							transform: 'scale(1.05)',
						},
					},
				}
			),
		}),
	});

// edit
const getEditGlobalStyles = (styles) =>
	cssObj({
		'.wp-block[data-type="supt/card-resource"]': {
			width:
				styles?.minWidth &&
				styles?.maxWidth &&
				`clamp(${styles?.minWidth}, 100%, ${styles?.maxWidth})`,

			'.supt-card': {
				height: '100%',
			},

			'.supt-cardResource__imageWrapper': {
				'&:hover, &:focus-within': {
					'.supt-cardResource__imagePlay': {
						opacity: 0,
					},
				},
			},

			'.supt-cardResource__imagePlay': {
				transition: `all ${transitions.mid} ease-out`,
			},

			'.supt-cardResource__image': {
				width: '100%',

				figure: {
					width: '100% !important',
				},

				'.editor-post-featured-image__preview img': {
					width: '100%',
					height: '100%',
				},
			},
		},
	});

export { getStyles, getModalStyles, getEditGlobalStyles };
